import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { featuredPriceValues, isListingFeatured, listingIdOf } from '../../util/featured';
import { sandboxToggleFeatured } from '../../util/api';

import css from './FeaturedSandboxToggle.module.css';

/**
 * Featured placement control. Updates listing publicData.featured / featuredUntil
 * the same way the homepage strip reads. Never a silent no-op.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.listing]
 * @param {Function} [props.onToggled]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const FeaturedSandboxToggle = props => {
  const { listing, onToggled, className } = props;
  const listingId = listingIdOf(listing);
  const [override, setOverride] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const featured = override ? !!override.featured : isListingFeatured(listing);
  const untilRaw = override ? override.featuredUntil : listing?.attributes?.publicData?.featuredUntil;
  const until = untilRaw ? new Date(untilRaw) : null;
  const untilLabel =
    until && !Number.isNaN(until.getTime())
      ? until.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null;

  const onToggle = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (inProgress) {
      return;
    }
    if (!listingId) {
      setFeedback({ type: 'err', id: 'DashboardPage.featuredMissingProfile' });
      return;
    }
    const next = !featured;
    setInProgress(true);
    setFeedback(null);
    sandboxToggleFeatured(listingId, next)
      .then(payload => {
        const data = payload?.data || {};
        const nowFeatured = typeof data.featured === 'boolean' ? data.featured : next;
        setOverride({
          featured: nowFeatured,
          featuredUntil: data.featuredUntil || (nowFeatured ? untilRaw : null),
        });
        setInProgress(false);
        setFeedback({
          type: 'ok',
          id: nowFeatured ? 'DashboardPage.featuredOnSuccess' : 'DashboardPage.featuredOffSuccess',
        });
        if (typeof onToggled === 'function') {
          onToggled(data);
        }
      })
      .catch(e => {
        setInProgress(false);
        setFeedback({
          type: 'err',
          id: 'DashboardPage.featuredFailed',
          text: e?.error || e?.message || null,
        });
      });
  };

  return (
    <div className={classNames(css.root, className)}>
      <div className={css.top}>
        <div>
          <h3 className={css.title}>{listing?.attributes?.title || listing?.title || null}</h3>
          <p className={css.meta}>
            {featured ? (
              untilLabel ? (
                <FormattedMessage id="DashboardPage.featuredActive" values={{ date: untilLabel }} />
              ) : (
                <FormattedMessage id="DashboardPage.featuredOn" />
              )
            ) : (
              <FormattedMessage id="DashboardPage.featuredInactive" />
            )}
          </p>
        </div>
        <span className={css.price}>
          <FormattedMessage id="DashboardPage.featuredCta" values={featuredPriceValues()} />
        </span>
      </div>
      {feedback ? (
        <p
          className={feedback.type === 'err' ? css.error : css.success}
          role={feedback.type === 'err' ? 'alert' : 'status'}
        >
          {feedback.text ? feedback.text : <FormattedMessage id={feedback.id} />}
        </p>
      ) : null}
      <button
        type="button"
        className={featured ? css.toggleOff : css.toggleOn}
        onClick={onToggle}
        disabled={inProgress}
      >
        {inProgress ? (
          <FormattedMessage id="DashboardPage.featuredSaving" />
        ) : (
          <FormattedMessage
            id={featured ? 'DashboardPage.featuredToggleOff' : 'DashboardPage.featuredToggleOn'}
          />
        )}
      </button>
    </div>
  );
};

export default FeaturedSandboxToggle;
